import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Dimensions } from "react-native";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  FlatList,
  Animated,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import colors from "../theme/colors";
import wordsData from "../../words.json";
import phrases from "../data/phrases";
import PhraseCard from "../components/PhraseCard";
import PhrasesModal from "../components/PhrasesModal";

const WordCard = ({ word, onAudioPress }) => {
  return (
    <View style={styles.wordCard} accessibilityLabel={`word-${word.id}`}>
      <View style={styles.wordContent}>
        <View style={styles.wordTextContainer}>
          <Text style={styles.arabicText}>{word.arabic}</Text>
          <Text style={styles.transliterationText}>{word.transliteration}</Text>
          <Text style={styles.translationText}>{word.translation}</Text>
        </View>
        <TouchableOpacity
          style={styles.audioButton}
          onPress={() => onAudioPress(word)}
          accessibilityLabel={`audio-${word.id}`}>
          <Ionicons name="volume-high" size={24} color={colors.primary} />
        </TouchableOpacity>
      </View>
    </View>
  );
};

const AlphabetNavbar = ({ currentLetter, onLetterPress, letterIndexMap, wordsData }) => {
  const scrollViewRef = useRef(null);

  // Use predefined Arabic alphabet letters
  const letters = [
    "أ",
    "ب",
    "ت",
    "ث",
    "ج",
    "ح",
    "خ",
    "د",
    "ذ",
    "ر",
    "ز",
    "س",
    "ش",
    "ص",
    "ض",
    "ط",
    "ظ",
    "ع",
    "غ",
    "ف",
    "ق",
    "ك",
    "ل",
    "م",
    "ن",
    "ه",
    "و",
    "ي",
  ];

  // Auto-scroll to current letter when it changes
  useEffect(() => {
    if (currentLetter && scrollViewRef.current) {
      const currentIndex = letters.indexOf(currentLetter);
      if (currentIndex !== -1) {
        // Calculate position to center the current letter
        const buttonWidth = 62; // Button width (50) + margin (12)
        const screenWidth = Dimensions.get("window").width;
        const scrollPosition = currentIndex * buttonWidth - screenWidth / 2 + buttonWidth / 2;

        scrollViewRef.current.scrollTo({
          x: Math.max(0, scrollPosition),
          animated: true,
        });
      }
    }
  }, [currentLetter, letters]);

  return (
    <View style={styles.alphabetNavbar}>
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.alphabetNavbarContent}>
        {letters.length > 0 ? (
          letters.map((letter, index) => {
            const hasWords = letterIndexMap && letterIndexMap[letter] !== undefined;
            return (
              <TouchableOpacity
                key={index}
                style={[
                  styles.alphabetButton,
                  currentLetter === letter && styles.alphabetButtonActive,
                  !hasWords && styles.alphabetButtonDisabled,
                ]}
                onPress={() => onLetterPress(letter)}>
                <Text
                  style={[
                    styles.alphabetButtonText,
                    currentLetter === letter && styles.alphabetButtonTextActive,
                    !hasWords && styles.alphabetButtonTextDisabled,
                  ]}>
                  {letter}
                </Text>
              </TouchableOpacity>
            );
          })
        ) : (
          <Text style={styles.alphabetButtonText}>No letters found</Text>
        )}
      </ScrollView>
    </View>
  );
};

const WordsScreen = () => {
  const [words, setWords] = useState([]);
  const [currentLetter, setCurrentLetter] = useState("");
  const [showAlphabetIndicator, setShowAlphabetIndicator] = useState(false);
  const [selectedPhrase, setSelectedPhrase] = useState(null);
  const [showPhrasesModal, setShowPhrasesModal] = useState(false);
  const [activeTab, setActiveTab] = useState("words"); // "words" or "proverbs"
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentProverbCategory, setCurrentProverbCategory] = useState("");
  const flatListRef = useRef(null);
  const scrollViewRef = useRef(null);
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    loadWords();

    // Set initial letter based on first available letter
    const sortedLetters = Object.keys(wordsData).sort((a, b) => a.localeCompare(b, "ar"));
    if (sortedLetters.length > 0) {
      setCurrentLetter(sortedLetters[0]);
    }
  }, []);

  const loadWords = () => {
    // Convert nested structure to flat array with proper indexing and IDs
    const flatWords = [];
    let globalIndex = 0;

    // Sort letters alphabetically
    const sortedLetters = Object.keys(wordsData).sort((a, b) => a.localeCompare(b, "ar"));

    sortedLetters.forEach((letter) => {
      const wordsForLetter = wordsData[letter] || [];
      wordsForLetter.forEach((word, wordIndex) => {
        flatWords.push({
          ...word,
          id: `${letter}_${wordIndex}`, // Unique ID: letter_wordIndex
          index: globalIndex++,
          letter: letter, // Add letter for reference
        });
      });
    });

    setWords(flatWords);
  };

  const getFirstLetter = (arabicText) => {
    return arabicText.charAt(0);
  };

  // Pre-compute letter to index mapping for better performance
  const letterIndexMap = useMemo(() => {
    const map = {};
    let currentIndex = 0;

    // Sort letters alphabetically
    const sortedLetters = Object.keys(wordsData).sort((a, b) => a.localeCompare(b, "ar"));

    sortedLetters.forEach((letter) => {
      const wordsForLetter = wordsData[letter] || [];
      if (wordsForLetter.length > 0) {
        map[letter] = currentIndex; // First word index for this letter
        currentIndex += wordsForLetter.length; // Move to next letter's starting index
      }
    });

    return map;
  }, [wordsData]);

  // Enhanced ID-based mapping system
  const wordIdMap = useMemo(() => {
    const map = {};
    words.forEach((word, index) => {
      map[word.id] = index; // Map word ID to array index
    });
    return map;
  }, [words]);

  // Function to scroll to specific word by ID
  const scrollToWordById = useCallback(
    (wordId) => {
      const targetIndex = wordIdMap[wordId];
      if (targetIndex !== undefined && flatListRef.current) {
        flatListRef.current.scrollToIndex({
          index: targetIndex,
          animated: true,
          viewPosition: 0.1,
        });
      }
    },
    [wordIdMap]
  );

  // Function to get word by ID
  const getWordById = useCallback(
    (wordId) => {
      return words.find((word) => word.id === wordId);
    },
    [words]
  );

  // Example: Scroll to specific word by ID
  const scrollToSpecificWord = useCallback(
    (wordId) => {
      console.log(`Scrolling to word with ID: ${wordId}`);
      scrollToWordById(wordId);
    },
    [scrollToWordById]
  );

  // Example: Get word info by ID
  const getWordInfo = useCallback(
    (wordId) => {
      const word = getWordById(wordId);
      if (word) {
        console.log(`Word found: ${word.arabic} (${word.translation})`);
        return word;
      } else {
        console.log(`Word not found with ID: ${wordId}`);
        return null;
      }
    },
    [getWordById]
  );

  const handleLetterPress = useCallback(
    (letter) => {
      setCurrentLetter(letter);

      // Use the pre-computed mapping for better accuracy
      const targetIndex = letterIndexMap[letter];

      if (targetIndex !== undefined && flatListRef.current) {
        // Calculate exact offset for more precise scrolling
        const itemHeight = 120; // Height of each word card
        const headerHeight = 160; // Header + navbar height
        const listPadding = 20; // Padding from listContainer
        const exactOffset = itemHeight * targetIndex + listPadding;

        // Add a small delay to ensure the state is updated
        setTimeout(() => {
          // Use scrollToOffset directly for more precise positioning
          flatListRef.current.scrollToOffset({
            offset: Math.max(0, exactOffset),
            animated: true,
          });
        }, 100);
      } else {
        // Show "no words" indicator for letters without words
        setShowAlphabetIndicator(true);
        setCurrentLetter(letter);

        // Animate the indicator
        Animated.sequence([
          Animated.timing(fadeAnim, {
            toValue: 1,
            duration: 200,
            useNativeDriver: true,
          }),
          Animated.delay(1500),
          Animated.timing(fadeAnim, {
            toValue: 0,
            duration: 300,
            useNativeDriver: true,
          }),
        ]).start(() => {
          setShowAlphabetIndicator(false);
        });
      }
    },
    [letterIndexMap, fadeAnim]
  );

  const handleAudioPress = useCallback((word) => {
    // TODO: Implement audio playback
    console.log("Playing audio for:", word.arabic);
  }, []);

  const handleIdealPress = useCallback((phrase) => {
    setSelectedPhrase(phrase);
    setShowPhrasesModal(true);
  }, []);

  const handleClosePhrasesModal = useCallback(() => {
    setShowPhrasesModal(false);
    setSelectedPhrase(null);
  }, []);

  // Proverb scroll handling
  const onProverbViewableItemsChanged = useCallback(
    ({ viewableItems }) => {
      if (viewableItems.length > 0 && activeTab === "proverbs") {
        const firstVisibleItem = viewableItems[0];
        const currentCategory = firstVisibleItem.item.category;

        setCurrentProverbCategory((prevCategory) => {
          if (currentCategory !== prevCategory) {
            // Auto-scroll to the highlighted category
            setTimeout(() => {
              scrollToCategory(currentCategory);
            }, 100);
            return currentCategory;
          }
          return prevCategory;
        });
      }
    },
    [activeTab, phrases]
  );

  const onProverbScroll = useCallback((event) => {
    // Additional scroll handling if needed
  }, []);

  // Function to scroll to category with smooth animation
  const scrollToCategory = useCallback(
    (category) => {
      const categoryIndex = Array.from(new Set(phrases.map((p) => p.category))).indexOf(category);
      if (categoryIndex !== -1) {
        const scrollPosition = Math.max(0, categoryIndex * 132 - 100);
        scrollViewRef.current?.scrollTo({
          x: scrollPosition,
          animated: true,
        });
      }
    },
    [phrases]
  );

  const proverbViewabilityConfig = useMemo(
    () => ({
      itemVisiblePercentThreshold: 50,
      minimumViewTime: 100,
    }),
    []
  );

  const renderWord = useCallback(
    ({ item }) => <WordCard word={item} onAudioPress={handleAudioPress} />,
    [handleAudioPress]
  );

  const onScrollToIndexFailed = useCallback((info) => {
    console.log("Scroll to index failed:", info);

    // Calculate approximate offset based on item height and header height
    const itemHeight = 120; // Approximate height of each word card
    const headerHeight = 160; // Header + navbar height
    const estimatedOffset = itemHeight * info.index - headerHeight;

    // Fallback: scroll to approximate position
    const wait = new Promise((resolve) => setTimeout(resolve, 500));
    wait.then(() => {
      if (flatListRef.current) {
        flatListRef.current.scrollToOffset({
          offset: Math.max(0, estimatedOffset), // Ensure offset is not negative
          animated: true,
        });
      }
    });
  }, []);

  const onViewableItemsChanged = useCallback(({ viewableItems }) => {
    if (viewableItems.length > 0) {
      const firstVisibleItem = viewableItems[0];
      const currentLetter = firstVisibleItem.item.letter; // Use the letter from the item

      setCurrentLetter((prevLetter) => {
        if (currentLetter !== prevLetter) {
          setShowAlphabetIndicator(true);

          // Animate the indicator
          Animated.sequence([
            Animated.timing(fadeAnim, {
              toValue: 1,
              duration: 200,
              useNativeDriver: true,
            }),
            Animated.delay(1500),
            Animated.timing(fadeAnim, {
              toValue: 0,
              duration: 300,
              useNativeDriver: true,
            }),
          ]).start(() => {
            setShowAlphabetIndicator(false);
          });

          return currentLetter;
        }
        return prevLetter;
      });
    }
  }, []);

  const viewabilityConfig = useMemo(
    () => ({
      itemVisiblePercentThreshold: 50,
      minimumViewTime: 100,
    }),
    []
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Words</Text>
      </View>

      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === "words" && styles.activeTab]}
          onPress={() => setActiveTab("words")}>
          <Text style={[styles.tabText, activeTab === "words" && styles.activeTabText]}>Words</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.tab, activeTab === "proverbs" && styles.activeTab]}
          onPress={() => setActiveTab("proverbs")}>
          <Text style={[styles.tabText, activeTab === "proverbs" && styles.activeTabText]}>
            Proverbs
          </Text>
        </TouchableOpacity>
      </View>

      {activeTab === "words" && (
        <>
          <AlphabetNavbar
            currentLetter={currentLetter}
            onLetterPress={handleLetterPress}
            letterIndexMap={letterIndexMap}
            wordsData={wordsData}
          />

          <FlatList
            ref={flatListRef}
            data={words}
            renderItem={renderWord}
            keyExtractor={(item) => item.id}
            contentContainerStyle={styles.listContainer}
            showsVerticalScrollIndicator={false}
            scrollEventThrottle={16}
            onViewableItemsChanged={onViewableItemsChanged}
            viewabilityConfig={viewabilityConfig}
            onScrollToIndexFailed={onScrollToIndexFailed}
            getItemLayout={(data, index) => ({
              length: 120, // Fixed height for each item
              offset: 120 * index + 20, // 20px padding from listContainer
              index,
            })}
          />
        </>
      )}

      {activeTab === "proverbs" && (
        <>
          {/* Sticky Category Navbar - positioned below tabs */}
          <View style={styles.proverbsCategoryNavbarSticky}>
            <ScrollView
              ref={scrollViewRef}
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.proverbsCategoryNavbarContent}>
              {Array.from(new Set(phrases.map((p) => p.category))).map((category) => (
                <TouchableOpacity
                  key={category}
                  style={[
                    styles.proverbsCategoryButton,
                    selectedCategory === category && styles.proverbsCategoryButtonActive,
                    currentProverbCategory === category && styles.proverbsCategoryButtonHighlighted,
                  ]}
                  onPress={() => {
                    setSelectedCategory(selectedCategory === category ? null : category);
                    // Auto-scroll to clicked category if it's not visible
                    scrollToCategory(category);
                  }}>
                  <Text
                    style={[
                      styles.proverbsCategoryButtonText,
                      selectedCategory === category && styles.proverbsCategoryButtonTextActive,
                      currentProverbCategory === category &&
                        styles.proverbsCategoryButtonTextHighlighted,
                    ]}>
                    {category}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>

          {/* Scrollable Content Container */}
          <View style={styles.proverbsContainer}>
            {/* Proverbs List */}
            <FlatList
              data={phrases.filter(
                (phrase) => !selectedCategory || phrase.category === selectedCategory
              )}
              renderItem={({ item }) => (
                <PhraseCard phrase={item} onIdealPress={handleIdealPress} />
              )}
              keyExtractor={(item) => item.id}
              contentContainerStyle={styles.proverbsListContainer}
              showsVerticalScrollIndicator={false}
              onViewableItemsChanged={onProverbViewableItemsChanged}
              viewabilityConfig={proverbViewabilityConfig}
              onScroll={onProverbScroll}
              scrollEventThrottle={16}
            />
          </View>
        </>
      )}

      {showAlphabetIndicator && currentLetter && (
        <Animated.View style={[styles.alphabetIndicator, { opacity: fadeAnim }]}>
          <Text style={styles.alphabetIndicatorText}>
            {letterIndexMap[currentLetter] !== undefined ? currentLetter : "No Words"}
          </Text>
        </Animated.View>
      )}

      <PhrasesModal
        visible={showPhrasesModal}
        phrase={selectedPhrase}
        onClose={handleClosePhrasesModal}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.secondary,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    backgroundColor: colors.secondary,
  },
  title: {
    fontSize: 28,
    fontWeight: "bold",
    color: colors.text.primary,
    fontFamily: "KhebratMusamim",
  },
  tabContainer: {
    flexDirection: "row",
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
  },
  tab: {
    flex: 1,
    paddingVertical: 16,
    alignItems: "center",
    borderBottomWidth: 3,
    borderBottomColor: "transparent",
  },
  activeTab: {
    borderBottomColor: colors.primary,
  },
  tabText: {
    fontSize: 16,
    fontWeight: "600",
    color: colors.gray,
    fontFamily: "KhebratMusamim",
  },
  activeTabText: {
    color: colors.primary,
  },
  proverbsContainer: {
    flex: 1,
    backgroundColor: colors.secondary,
  },
  proverbsTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 8,
    marginTop: 20,
    paddingHorizontal: 20,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  proverbsSubtitle: {
    fontSize: 16,
    color: colors.gray,
    marginBottom: 20,
    paddingHorizontal: 20,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  proverbsListContainer: {
    paddingBottom: 20,
    paddingTop: 80,
    paddingHorizontal: 20,
  },
  categoryFilter: {
    marginBottom: 20,
  },
  categoryFilterContainer: {
    // paddingHorizontal: 20,
  },
  categoryChip: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 12,
    borderRadius: 20,
    backgroundColor: colors.lightGray,
    borderWidth: 1,
    borderColor: colors.lightGray,
  },
  categoryChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  categoryChipText: {
    fontSize: 14,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
    fontWeight: "500",
  },
  categoryChipTextActive: {
    color: colors.white,
  },
  proverbsCategoryNavbar: {
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
    height: 60,
    marginBottom: 20,
  },
  proverbsCategoryNavbarSticky: {
    position: "absolute",
    top: 140, // Position below tabs and header
    left: 0,
    right: 0,
    zIndex: 1000,
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
    height: 60,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  proverbsCategoryNavbarContent: {
    paddingHorizontal: 10,
    paddingVertical: 10,
    alignItems: "center",
  },
  proverbsCategoryButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginHorizontal: 6,
    borderRadius: 25,
    backgroundColor: colors.lightGray,
    minWidth: 120,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.lightGray,
    flexShrink: 0,
  },
  proverbsCategoryButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  proverbsCategoryButtonHighlighted: {
    backgroundColor: colors.accent,
    borderColor: colors.accent,
    transform: [{ scale: 1.05 }],
  },
  proverbsCategoryButtonText: {
    fontSize: 14,
    fontWeight: "600",
    color: colors.gray,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  proverbsCategoryButtonTextActive: {
    color: colors.white,
  },
  proverbsCategoryButtonTextHighlighted: {
    color: colors.white,
  },

  alphabetNavbar: {
    backgroundColor: colors.white,
    borderBottomWidth: 1,
    borderBottomColor: colors.lightGray,
    height: 60,
  },
  alphabetNavbarContent: {
    paddingHorizontal: 10,
    paddingVertical: 10,
    alignItems: "center",
  },
  alphabetButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginHorizontal: 6,
    borderRadius: 25,
    backgroundColor: colors.lightGray,
    minWidth: 50,
    height: 40,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.lightGray,
  },
  alphabetButtonActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  alphabetButtonDisabled: {
    backgroundColor: colors.lightGray,
    borderColor: colors.lightGray,
    opacity: 0.5,
  },
  alphabetButtonText: {
    fontSize: 18,
    fontWeight: "bold",
    color: colors.text.primary,
    fontFamily: "KhebratMusamim",
    textAlign: "center",
  },
  alphabetButtonTextActive: {
    color: colors.white,
  },
  alphabetButtonTextDisabled: {
    color: colors.gray,
    opacity: 0.6,
  },
  wordCountText: {
    fontSize: 12,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
  },
  listContainer: {
    padding: 20,
  },
  wordCard: {
    backgroundColor: colors.white,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: colors.lightGray,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  wordContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  wordTextContainer: {
    flex: 1,
  },
  arabicText: {
    fontSize: 20,
    fontWeight: "bold",
    color: colors.text.primary,
    marginBottom: 4,
    fontFamily: "KhebratMusamim",
    textAlign: "left",
  },
  transliterationText: {
    fontSize: 16,
    color: colors.primary,
    marginBottom: 2,
    fontFamily: "KhebratMusamim",
  },
  translationText: {
    fontSize: 14,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
  },
  debugText: {
    fontSize: 10,
    color: colors.gray,
    fontFamily: "KhebratMusamim",
    opacity: 0.5,
  },
  audioButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.lightPrimary,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 12,
  },
  alphabetIndicator: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: [{ translateX: -50 }, { translateY: -50 }],
    backgroundColor: colors.primary,
    borderRadius: 50,
    width: 100,
    height: 100,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
    zIndex: 1000,
  },
  alphabetIndicatorText: {
    fontSize: 36,
    fontWeight: "bold",
    color: colors.white,
    fontFamily: "KhebratMusamim",
  },
});

export default WordsScreen;
